import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * 全局下滑渐显滚动动画 Hook
 * 监听视口滚动，当页面向下滚动各区块进入视口时，触发优雅平滑的上升渐显动效
 */
export function useScrollReveal() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      return;
    }

    // 需要监听下滑渐显的目标元素列表
    const selectors = [
      ".section-heading",
      ".service-grid > article",
      ".benefit-grid > article",
      ".story-visual",
      ".story-copy",
      ".steps-grid > li",
      ".platform-grid > div",
      ".platform-list > div",
      ".faq-intro",
      ".faq-list details",
      ".cta-panel",
      ".pricing-grid > article",
      ".client-grid > article",
      ".install-section .story-copy",
      ".waitlist-card",
      ".info-banner",
      ".reveal-on-scroll",
    ];

    const elements = document.querySelectorAll<HTMLElement>(selectors.join(", "));

    elements.forEach((el) => {
      el.classList.add("reveal-init");

      // 对网格内的子项添加错落有致的阶梯延迟（Stagger Delay）
      const parent = el.parentElement;
      if (parent) {
        if (
          parent.classList.contains("service-grid") ||
          parent.classList.contains("benefit-grid") ||
          parent.classList.contains("steps-grid") ||
          parent.classList.contains("pricing-grid") ||
          parent.classList.contains("client-grid") ||
          parent.classList.contains("platform-list") ||
          parent.classList.contains("faq-list")
        ) {
          const index = Array.from(parent.children).indexOf(el);
          const delayClass = `reveal-delay-${(index % 4) + 1}`;
          el.classList.add(delayClass);
        }
      }
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting || entry.boundingClientRect.top < window.innerHeight) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      {
        root: null,
        rootMargin: "0px 0px -40px 0px", // 元素距离视口底部40px时即触发渐显
        threshold: 0.05,
      },
    );

    elements.forEach((el) => {
      // 若首屏或刷新后已位于可视区域内或上方，立即显现；否则交由观察器监听下滑进入
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight) {
        el.classList.add("is-visible");
      } else {
        observer.observe(el);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [location.pathname]);
}
